import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';

const TEXTAREA_CLS =
  'w-full resize-none rounded border border-border bg-background px-2 py-1 text-xs ' +
  'focus:outline-none focus:ring-1 focus:ring-ring';

/**
 * "Instruct about this" on a clip/keyframe/music tile: opens an inline comment box
 * pinned to the asset and sends the note straight to the Director (which turns it
 * into a Work Queue item for that shot). No-ops if there's no production id.
 */
export default function InstructButton({
  productionId,
  assetRef,
  label,
}: {
  productionId?: string;
  assetRef: string;
  label: string;
}) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    const body = text.trim();
    if (!body || !productionId || sending) return;
    setSending(true);
    try {
      await apiFetch(`/api/chat/${productionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: body, asset_ref: assetRef }),
      });
      setSent(true);
      setText('');
      setTimeout(() => {
        setOpen(false);
        setSent(false);
      }, 1200);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title={t('tile.instructTitle')}
        className="flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-medium text-white hover:bg-primary"
      >
        <MessageSquare className="h-2.5 w-2.5" />
        {t('tile.instruct')}
      </button>
      {open && (
        <div
          className="absolute left-0 top-6 z-30 w-56 rounded-md border border-border bg-card p-2 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-1 flex items-center gap-1 text-[10px] text-muted-foreground">
            <span>{t('tile.instructRe')}</span>
            <span className="font-mono font-medium">{label}</span>
          </div>
          {sent ? (
            <div className="py-2 text-center text-xs font-medium text-success">{t('tile.instructSent')}</div>
          ) : (
            <>
              <textarea
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder={t('tile.instructPlaceholder')}
                rows={2}
                className={TEXTAREA_CLS}
              />
              <div className="mt-1 flex justify-end gap-1">
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => setOpen(false)}>
                  {t('action.cancel', { ns: 'common' })}
                </Button>
                <Button size="sm" className="h-6 px-2 text-[10px]" onClick={submit} disabled={sending || !text.trim()}>
                  <Send className="mr-0.5 h-3 w-3" />
                  {t('tile.send')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
